import LoadError from "@/components/LoadError";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DollarSign, MapPin, Circle } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Card, Pill, Spinner, PageTitle, useToast } from "@/components/earnos/kit";
import { Orb } from "@/components/earnos/Orb";

export default function Opportunities() {
  const toast = useToast();
  const navigate = useNavigate();
  const [growth, setGrowth] = useState([]);
  const [growthError, setGrowthError] = useState("");
  const [starting, setStarting] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try { setData(await apiFetch("/opportunities")); }
    catch (e) {
      setLoadError(e.message || "Service unavailable. Please retry."); toast.show(e?.message || "Failed to load", "error"); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let active = true;
    apiFetch("/economy/growth/actions").then((result) => {
      if (active) setGrowth(result.actions || []);
    }).catch((error) => { if (active) setGrowthError(error.message || "Growth actions unavailable"); });
    return () => { active = false; };
  }, []);

  const startGrowth = async (key) => {
    if (starting) return;
    setStarting(key);
    try {
      const result = await apiFetch("/economy/growth/start", { method: "POST", body: { action_key: key } });
      toast.show("Growth mission started. Review actual work and approvals in Mission Control.", "success");
      navigate("/mission?id=" + encodeURIComponent(result.mission_id));
    } catch (error) { toast.show(error.message || "Unable to start mission", "error"); }
    finally { setStarting(""); }
  };

  const save = async (id) => {
    await apiFetch(`/opportunities/${id}/save`, { method: "POST" });
    toast.show("Opportunity saved", "success");
    load();
  };

  if (loadError && !loading) return <LoadError title="Opportunities" message={loadError} onRetry={load} />;

  return (
    <div className="animate-fade-in">
      <PageTitle>Opportunities</PageTitle>
      <p className="text-base text-ink-2 mt-1 max-w-2xl">Legitimate demand matched to your engine, skills and boundaries.</p>

      <section className="mt-7" aria-labelledby="growth-actions-title">
        <h2 id="growth-actions-title" className="text-xl font-bold text-ink">Income growth actions EARNOS can prepare</h2>
        <p className="mt-2 text-sm text-ink-2">These are personalized work suggestions, not confirmed customers or promised income. Pick one and EARNOS opens a real mission; any external change requires separate approval.</p>
        {growthError && <p role="status" className="mt-3 text-sm text-ink-2">{growthError}</p>}
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {growth.map((g) => (
            <Card key={g.action_key}>
              <Pill label="SUGGESTED — NOT EARNED" color="#F59E0B" />
              <h3 className="mt-3 text-lg font-bold text-ink">{g.title}</h3>
              <p className="mt-2 text-sm text-ink-2">{g.why_this_fits}</p>
              <p className="mt-3 text-xs text-ink-3">Success check: {g.success_signal}</p>
              <button data-testid={"growth-start-" + g.action_key} disabled={Boolean(starting)}
                onClick={() => startGrowth(g.action_key)}
                className="mt-4 h-11 w-full rounded-xl bg-brand px-4 text-white font-bold disabled:opacity-40">
                {starting === g.action_key ? "Starting mission…" : "Have EARNOS prepare this"}
              </button>
            </Card>
          ))}
        </div>
      </section>

      {loading ? (
        <Spinner className="py-24" />
      ) : data?.status === "demo" ? (
        <div className="mt-6">
          <div className="mb-5"><Pill label="DEMO — sample opportunities" color="#F59E0B" /></div>
          <div className="grid md:grid-cols-2 gap-4">
            {data.opportunities.map((o) => (
              <Card key={o.opportunity_id}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-lg font-bold text-ink flex-1">{o.title}</h3>
                  {o.match_score ? <span className="px-2 py-0.5 rounded-full bg-ok/20 text-ok text-sm font-bold shrink-0">{o.match_score}% match</span> : null}
                </div>
                <p className="text-base text-ink-2 mt-2 leading-relaxed">{o.description}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {o.budget ? <span className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-surface-3 text-sm text-ink-2"><DollarSign size={13} />${o.budget} {o.compensation_model}</span> : null}
                  <span className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-surface-3 text-sm text-ink-2"><MapPin size={13} />{o.location || "Remote"}</span>
                </div>
                <button onClick={() => save(o.opportunity_id)} data-testid={`save-opp-${o.opportunity_id}`} className="w-full mt-5 h-11 rounded-full bg-surface-3 border border-line-strong text-ink font-bold hover:border-brand-light transition-colors">
                  {o.status === "saved" ? "Saved" : "Save opportunity"}
                </button>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center text-center py-14 max-w-xl mx-auto">
          <Orb state="thinking" size={110} />
          <h2 className="text-xl font-bold text-ink mt-6">Opportunity Exchange is being prepared</h2>
          <p className="text-base text-ink-2 mt-2 leading-relaxed">{data?.message || "Live opportunities require a connected provider and are not enabled yet."}</p>
          <Card className="mt-8 w-full text-left">
            <h3 className="text-lg font-bold text-ink mb-3">How it will work</h3>
            {[
              "Verified requests and projects will be matched to your capabilities and boundaries.",
              "Every match will explain why it fits you.",
              "You approve outreach before anything is sent — nothing is automatic.",
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-2 mb-2">
                <Circle size={7} className="text-brand-light mt-2 shrink-0 fill-current" />
                <p className="text-base text-ink-3 leading-relaxed">{t}</p>
              </div>
            ))}
            <p className="text-sm text-brand-light mt-3">Tip: turn on Demo mode in You → Settings to preview sample opportunities.</p>
          </Card>
        </div>
      )}
    </div>
  );
}
